import type { DayView, POI, Schedule } from "@poi-planner/shared";
import { hasUsableCoordinates, resolveNoteMapPoi } from "./dayMapPois";
import { addDaysToDate } from "./zonedTime";

export interface PlannerRouteLine {
  date: string;
  coordinates: [number, number][];
  poiIds: string[];
}

export interface PlannerMapContext {
  pois: POI[];
  routeLines: PlannerRouteLine[];
  routeOrderByPoiId: Map<string, number>;
  unplottableCount: number;
}

export function buildPlannerMapContext(
  dayViews: DayView[],
  schedule: Schedule,
  pois: POI[],
): PlannerMapContext {
  const byId = new Map(pois.map((poi) => [poi.id, poi]));
  const ordered: POI[] = [];
  const seen = new Set<string>();
  const missing = new Set<string>();
  const routeLines: PlannerRouteLine[] = [];
  const routeOrderByPoiId = new Map<string, number>();

  const add = (poiId: string | null | undefined) => {
    if (!poiId || seen.has(poiId)) return;
    seen.add(poiId);
    const poi = byId.get(poiId);
    if (!poi || !hasUsableCoordinates(poi)) {
      missing.add(poiId);
      return;
    }
    ordered.push(poi);
  };

  for (const dayView of dayViews) {
    const routePois: POI[] = [];
    const routeSeen = new Set<string>();
    for (const block of dayView.blocks) {
      if (block.type === "poi") {
        const poi = byId.get(block.scheduledPOI.poiId);
        add(block.scheduledPOI.poiId);
        if (poi && hasUsableCoordinates(poi) && !routeSeen.has(poi.id)) {
          routeSeen.add(poi.id);
          routePois.push(poi);
          if (!routeOrderByPoiId.has(poi.id)) {
            routeOrderByPoiId.set(poi.id, routePois.length);
          }
        }
        continue;
      }
      const notePoi = resolveNoteMapPoi(
        block.note.startPoiId ? byId.get(block.note.startPoiId) : undefined,
        block.note.endPoiId ? byId.get(block.note.endPoiId) : undefined,
      );
      add(notePoi?.id);
    }
    for (const block of dayView.allDayBlocks) {
      const notePoi = resolveNoteMapPoi(
        block.note.startPoiId ? byId.get(block.note.startPoiId) : undefined,
        block.note.endPoiId ? byId.get(block.note.endPoiId) : undefined,
      );
      add(notePoi?.id);
    }

    add(schedule.sleep[addDaysToDate(dayView.date, -1)]);
    add(schedule.sleep[dayView.date]);

    if (routePois.length >= 2) {
      routeLines.push({
        date: dayView.date,
        coordinates: routePois.map((poi) => [poi.lng!, poi.lat!]),
        poiIds: routePois.map((poi) => poi.id),
      });
    }
  }

  return {
    pois: ordered,
    routeLines,
    routeOrderByPoiId,
    unplottableCount: missing.size,
  };
}
